import { useCallback, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

export interface TourStep {
  element: string;
  popover: {
    title: string;
    description: string;
    side?: 'top' | 'bottom' | 'left' | 'right';
    align?: 'start' | 'center' | 'end';
    onNext?: () => void;
  };
  highlight?: boolean;
}

export interface TourConfig {
  showProgress?: boolean;
  allowClose?: boolean;
  allowKeyboardControl?: boolean;
  disableActiveInteraction?: boolean;
  animate?: boolean;
  smoothScroll?: boolean;
  onDestroyed?: () => void;
  onHighlighted?: (element: Element | undefined) => void;
  onDeselected?: (element: Element | undefined) => void;
}

export interface PageTourData {
  hasRecords: boolean;
  recordCount: number;
  firstRecordId?: string;
}

export const usePageTour = () => {
  const driverRef = useRef<any>(null);

  const stopTour = useCallback(() => {
    if (driverRef.current) {
      try {
        driverRef.current.destroy();
      } catch (error) {
        console.warn('Error destroying tour:', error);
      } finally {
        driverRef.current = null;
      }
    }
  }, []);

  const startTour = useCallback((
    steps: TourStep[],
    config: TourConfig = {}
  ) => {
    // Stop any existing tour first and wait for cleanup
    stopTour();
    
    // Add a longer delay to ensure proper cleanup before starting new tour
    setTimeout(() => {
      // Force cleanup of any remaining Driver.js elements
      const driverElements = document.querySelectorAll('[data-driver-highlighted-element]');
      driverElements.forEach(el => el.removeAttribute('data-driver-highlighted-element'));
      
      const overlays = document.querySelectorAll('.driver-overlay, .driver-popover, .driver-popover-tip');
      overlays.forEach(el => el.remove());
      
      const driverClasses = document.querySelectorAll('.driver-highlighted-element, .driver-popover, .driver-overlay');
      driverClasses.forEach(el => el.remove());
      const defaultConfig: TourConfig = {
        showProgress: true,
        allowClose: true,
        allowKeyboardControl: true,
        disableActiveInteraction: false,
        animate: true,
        smoothScroll: true,
        onHighlighted: (element: Element | undefined) => {
          if (element) {
            // Center the element in the viewport
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            });
          }
          config.onHighlighted?.(element);
        },
        ...config
      };

      const driverObj = driver({
        showProgress: defaultConfig.showProgress,
        allowClose: defaultConfig.allowClose,
        allowKeyboardControl: defaultConfig.allowKeyboardControl,
        disableActiveInteraction: defaultConfig.disableActiveInteraction,
        animate: defaultConfig.animate,
        smoothScroll: defaultConfig.smoothScroll,
        steps: steps.map(step => ({
          element: step.element,
          popover: {
            title: step.popover.title,
            description: step.popover.description,
            side: step.popover.side || 'right',
            align: step.popover.align || 'start',
            onNext: step.popover.onNext
          },
          highlight: step.highlight !== false
        })),
        onDestroyed: () => {
          driverRef.current = null;
          // Scroll to top when tour is completed
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          defaultConfig.onDestroyed?.();
        },
        onHighlighted: (element: Element | undefined) => {
          defaultConfig.onHighlighted?.(element);
        },
        onDeselected: (element: Element | undefined) => {
          defaultConfig.onDeselected?.(element);
        }
      });

      driverRef.current = driverObj;
      driverObj.drive();
    }, 200); // 200ms delay for cleanup
  }, [stopTour]);

  // Cleanup function for component unmount or page navigation
  const cleanup = useCallback(() => {
    stopTour();
    
    // Additional cleanup if needed
    if (driverRef.current) {
      driverRef.current = null;
    }
    
    // Force cleanup of any remaining Driver.js elements
    const driverElements = document.querySelectorAll('[data-driver-highlighted-element]');
    driverElements.forEach(el => el.removeAttribute('data-driver-highlighted-element'));
    
    // Remove any Driver.js overlays and popovers
    const overlays = document.querySelectorAll('.driver-overlay, .driver-popover, .driver-popover-tip');
    overlays.forEach(el => el.remove());
    
    // Remove any Driver.js backdrop elements
    const backdrops = document.querySelectorAll('[data-driver-backdrop]');
    backdrops.forEach(el => el.remove());
    
    // Reset any elements that might have been modified by Driver.js
    const highlightedElements = document.querySelectorAll('[data-driver-highlighted-element]');
    highlightedElements.forEach(el => {
      el.removeAttribute('data-driver-highlighted-element');
      if (el instanceof HTMLElement) {
        el.style.position = '';
        el.style.zIndex = '';
      }
    });
    
    // Additional cleanup: remove any remaining Driver.js classes
    const driverClasses = document.querySelectorAll('.driver-highlighted-element, .driver-popover, .driver-overlay');
    driverClasses.forEach(el => el.remove());
  }, [stopTour]);

  return {
    startTour,
    stopTour,
    cleanup,
    isActive: !!driverRef.current,
    driverRef
  };
};
